import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type Integration,
} from "@repo/app-types/entities/integration";
import { countRecordingImportCustomerRows } from "@repo/app-types/entities/recordingImport";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useRecordingImportStatusCountsQuery } from "../../../api/entities/recordingImport.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectAllIntegrations } from "../../integrations/integrationsSlice.js";
import type { IntegrationConfig } from "./integrationConfigs.js";
import { IntegrationLogo } from "./integrationLogo.js";

/** What the Settings and Failed imports tabs share. */
export interface AlpharunOperatedIntegrationDetailOutletContext {
  integration: Integration;
  config: IntegrationConfig;
}

export function useAlpharunOperatedIntegrationDetailOutletContext(): AlpharunOperatedIntegrationDetailOutletContext {
  return useOutletContext<AlpharunOperatedIntegrationDetailOutletContext>();
}

/**
 * Whether Alpharun has the import running (vs paused). The boot payload
 * carries only `is_enabled` of these integrations' settings; the API upload
 * integration has none and is never paused.
 */
export function checkAlpharunOperatedIntegrationEnabled(
  integration: Integration,
): boolean {
  if (integration.provider === IntegrationProvider.API_RECORDING_UPLOAD) {
    return true;
  }
  return (
    "is_enabled" in integration.settings &&
    integration.settings.is_enabled === true
  );
}

/**
 * The detail page of an integration Alpharun operates (NICE CXone and
 * RingCentral, set up by script; the recording import API, created by the
 * first upload): a read-only **Settings** tab and a **Failed imports** tab over
 * the integration's `recording_import` rows. A company without the row is
 * redirected back to the integrations overview (e.g. a stale deep link).
 */
export const AlpharunOperatedIntegrationDetailSection: FC<{
  config: IntegrationConfig;
}> = ({ config }) => {
  const integration =
    useAppSelector(selectAllIntegrations).find(
      (candidate) => candidate.provider === config.provider,
    ) ?? null;
  const importCounts = useRecordingImportStatusCountsQuery(
    integration ? { integrationId: integration.id } : skipToken,
  );

  if (!integration) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const importIssueCount = importCounts.data
    ? countRecordingImportCustomerRows(importCounts.data)
    : 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    {
      label:
        importIssueCount > 0
          ? `Failed imports (${importIssueCount.toLocaleString()})`
          : "Failed imports",
      to: "failed-imports",
    },
  ];

  const context: AlpharunOperatedIntegrationDetailOutletContext = {
    integration,
    config,
  };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo provider={integration.provider} />
              <span className="truncate">{config.name}</span>
            </span>
          }
          tabs={<RouteTabs ariaLabel={`${config.name} sections`} tabs={tabs} />}
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
